import { AccountIdentifier } from '@icp-sdk/canisters/ledger/icp'
import { Principal } from '@icp-sdk/core/principal'
import React, { PropsWithChildren, createContext, useEffect, useMemo, useState } from 'react'
import { config } from '../appConfig'
import ChainFusionClient from '../clients/ChainFusionClient'
import plugbookClient, { PlugbookRecord } from '../clients/PlugbookClient'
import { ETH_CHAIN_ID } from '../constants/business'
import { Chain } from '../interfaces/common'
import { logger } from '../logger'
import { useCurrentWallet, useWalletBTCAddresses, useWalletProperties } from '../state'

export interface Wallet {
  principalId: string
  accountId: string
  name: string
  ethAddress?: string
  btcAddress?: string
  solAddress?: string
  dogeAddress?: string
  plugbook?: PlugbookRecord
}

interface WalletContextType {
  wallet: Wallet
  isWalletReady: boolean
}

export const WalletContext = createContext<WalletContextType>(null as any)

export const walletFromPrincipal = (principalId: string) => {
  const {
    walletNames,
    walletEthAddresses,
    walletSolAddresses,
    walletDogeAddresses,
    walletPlugbookRecords,
  } = useWalletProperties.getState()

  const { walletBTCAddresses } = useWalletBTCAddresses.getState()

  const principal = Principal.from(principalId)
  const accountId = AccountIdentifier.fromPrincipal({ principal })

  return {
    principalId,
    accountId: accountId.toHex(),
    name: walletNames[principalId],
    ethAddress: walletEthAddresses[principalId],
    btcAddress: walletBTCAddresses[principalId],
    solAddress: walletSolAddresses[principalId],
    dogeAddress: walletDogeAddresses[principalId],
    plugbook: walletPlugbookRecords[principalId],
  }
}

export const ensureChainFusionAddresses = async (principalId: string) => {
  const {
    walletEthAddresses,
    setWalletEthAddress,
    walletSolAddresses,
    setWalletSolAddress,
    walletDogeAddresses,
    setWalletDogeAddress,
  } = useWalletProperties.getState()
  const { walletBTCAddresses, setWalletBTCAddress } = useWalletBTCAddresses.getState()

  const chainFusion = new ChainFusionClient(config.getAgent())
  const principal = Principal.from(principalId)

  let ethAddress: string | Promise<string> = walletEthAddresses[principalId]
  if (!ethAddress) {
    ethAddress = chainFusion.getEthAddress(principal)
  }

  let btcAddress: string | Promise<string> = walletBTCAddresses[principalId]
  if (!btcAddress) {
    btcAddress = chainFusion.getBtcAddress(principal)
  }

  let solAddress: string | Promise<string> = walletSolAddresses[principalId]
  if (!solAddress) {
    solAddress = chainFusion.getSolAddress(principal)
  }

  let dogeAddress: string | Promise<string> = walletDogeAddresses[principalId]
  if (!dogeAddress) {
    dogeAddress = chainFusion.getDogeAddress(principal)
  }

  await Promise.all(
    [
      { address: ethAddress, setAddress: setWalletEthAddress },
      { address: btcAddress, setAddress: setWalletBTCAddress },
      { address: solAddress, setAddress: setWalletSolAddress },
      { address: dogeAddress, setAddress: setWalletDogeAddress },
    ].map(async ({ address, setAddress }) => {
      try {
        setAddress(principalId, await address)
      } catch (e) {
        logger.error('Failed to get address', { originalError: e })
      }
    })
  )
}

export const ensurePlugbookRecord = async (principalId: string) => {
  await ensureChainFusionAddresses(principalId).catch(logger.error)

  let plugbook = await plugbookClient.getNamedAddressForPrincipal(principalId)
  if (!plugbook) {
    plugbook = await plugbookClient.registerUser(principalId)
  }

  const { walletBTCAddresses } = useWalletBTCAddresses.getState()
  const { walletEthAddresses, walletSolAddresses, walletDogeAddresses, setWalletPlugbookRecord } =
    useWalletProperties.getState()

  const addresses = [
    { chain: Chain.BTC, address: walletBTCAddresses[principalId] },
    { chain: Chain.SOL, address: walletSolAddresses[principalId] },
    { chain: Chain.DOGE, address: walletDogeAddresses[principalId] },
    { chain: ETH_CHAIN_ID.toString(), address: walletEthAddresses[principalId] },
  ].filter(({ address }) => !!address)

  const isUpdated = addresses.every(({ chain, address }) =>
    plugbook?.addresses.find((a) => a.chain === chain && a.address === address)
  )

  if (!isUpdated) {
    plugbook = await plugbookClient.updateNamedAddress({ ...plugbook, addresses }, principalId)
  }

  setWalletPlugbookRecord(principalId, plugbook)
  return plugbook
}

const WalletProvider = ({ children }: PropsWithChildren) => {
  const [isWalletReady, setIsWalletReady] = useState(false)
  const { currentWallet } = useCurrentWallet()

  const {
    walletNames,
    walletEthAddresses,
    walletSolAddresses,
    walletDogeAddresses,
    walletPlugbookRecords,
  } = useWalletProperties()

  const { walletBTCAddresses } = useWalletBTCAddresses()

  useEffect(() => {
    setIsWalletReady(false)
    ensurePlugbookRecord(currentWallet)
      .catch(logger.error)
      .finally(() => setIsWalletReady(true))
  }, [currentWallet])

  const value: WalletContextType = useMemo(
    () => ({
      wallet: walletFromPrincipal(currentWallet),
      isWalletReady,
    }),
    [
      currentWallet,
      isWalletReady,
      walletNames,
      walletEthAddresses,
      walletBTCAddresses,
      walletSolAddresses,
      walletDogeAddresses,
      walletPlugbookRecords,
    ]
  )

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
}

export default WalletProvider
